import React from 'react'
import { Box, Card, CardContent, useTheme } from '@mui/material'
import ReactECharts from 'echarts-for-react'
import type { KeyStatSurveyItem } from '../../../../models/Report'
import { getReportTitleDisplayItems } from '../reportTitles'
import { type ReportChartType } from '../../types'
import { reportChartTypeToCalculationName } from '../constants'
import { useReportsContext } from '../../ReportsContext'
import { calculateChartHeight, getBarChartColors } from './BarChart/helper'
import { formatValue, getAvailableSeriesIndexes, getChartLabelWidth, getScrollableChartWidth } from '../helper'

interface StatComparisonChartProps {
  titleComponent: React.ReactNode
  statisticBox: React.ReactNode
  report: KeyStatSurveyItem
  titles?: string[]
  chartType: Exclude<ReportChartType, 'distribution'>
  isHorizontalLayout?: boolean
}

const getLinearScaleBounds = (report: KeyStatSurveyItem): { min?: number, max?: number } => {
  if (!Array.isArray(report.options) || report.options.length === 0) {
    return {}
  }

  const weights = report.options
    .flatMap((option) => option.weights ?? [])
    .filter((weight): weight is number => typeof weight === 'number' && Number.isFinite(weight))

  if (!weights.length) {
    return {}
  }

  return {
    min: Math.min(...weights),
    max: Math.max(...weights)
  }
}

const StatComparisonChart: React.FC<StatComparisonChartProps> = ({
  titleComponent,
  statisticBox,
  report,
  titles = [],
  chartType,
  isHorizontalLayout = false
}) => {
  const theme = useTheme()
  const { selectedTrend } = useReportsContext()
  const calculationName = reportChartTypeToCalculationName[chartType]
  const values = report.calculations?.find((calc) => calc.name === calculationName)?.values ?? []
  const displayTitleItems = getReportTitleDisplayItems(titles)
  const availableSeriesIndexes = getAvailableSeriesIndexes(report, titles.length)
  const displayTitles = availableSeriesIndexes.map((index) => displayTitleItems[index]?.displayTitle ?? titles[index] ?? `Series ${index + 1}`)
  const unitSeriesColors = getBarChartColors(selectedTrend)
  const horizontalLabelWidth = getChartLabelWidth(displayTitles, true)
  const chartWidth = getScrollableChartWidth(displayTitles, isHorizontalLayout)
  const chartHeight = calculateChartHeight(displayTitles.length, isHorizontalLayout, false)
  const data = displayTitles.map((title, filteredIndex) => {
    const index = availableSeriesIndexes[filteredIndex]

    return ({
      name: title,
      value: typeof values[index] === 'number' ? values[index] : null,
      itemStyle: {
        color: unitSeriesColors[index % unitSeriesColors.length]
      }
    })
  })
  const isPercentChart = chartType === 'top2' || chartType === 'bottom2'
  const linearScaleBounds = chartType === 'average' ? getLinearScaleBounds(report) : {}

  const option = {
    grid: isHorizontalLayout
      ? { left: horizontalLabelWidth + 24, right: 48, top: 36, bottom: 44 }
      : { left: 60, right: 20, top: 30, bottom: 88 },
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const value = params.value
        const formattedValue = value == null ? 'N/A' : isPercentChart ? `${formatValue(value)}%` : formatValue(value)
        return `${params.name}<br/>${calculationName}: <b>${formattedValue}</b>`
      }
    },
    xAxis: isHorizontalLayout
      ? {
          type: 'value',
          min: isPercentChart ? 0 : linearScaleBounds.min,
          max: isPercentChart ? 100 : linearScaleBounds.max,
          axisLine: { show: false },
          axisLabel: {
            color: theme.palette.text.secondary,
            fontSize: 14,
            margin: 14,
            formatter: isPercentChart ? '{value}%' : '{value}'
          },
          axisTick: { show: false },
          splitLine: { show: true }
        }
      : {
          type: 'category',
          data: displayTitles,
          axisLine: { show: false },
          axisLabel: {
            color: theme.palette.text.secondary,
            fontSize: 14,
            fontWeight: 500,
            align: 'center',
            interval: 0,
            margin: 18,
            overflow: 'truncate',
            width: 110,
            showMaxLabel: true,
            showMinLabel: true
          },
          axisTick: { show: false },
          z: 10
        },
    yAxis: isHorizontalLayout
      ? {
          type: 'category',
          data: displayTitles,
          axisLine: { show: false },
          axisLabel: {
            color: theme.palette.text.secondary,
            fontSize: 14,
            fontWeight: 500,
            margin: 12,
            width: horizontalLabelWidth,
            overflow: 'truncate'
          },
          axisTick: { show: false },
          splitLine: { show: false }
        }
      : {
          type: 'value',
          min: isPercentChart ? 0 : linearScaleBounds.min,
          max: isPercentChart ? 100 : linearScaleBounds.max,
          axisLine: { show: false },
          axisLabel: {
            color: theme.palette.text.secondary,
            fontSize: 14,
            margin: 20,
            formatter: isPercentChart ? '{value}%' : '{value}'
          },
          axisTick: { show: false },
          splitLine: { show: true }
        },
    series: [
      {
        type: 'bar',
        data,
        itemStyle: {
          borderRadius: isHorizontalLayout ? [0, 10, 10, 0] : [10, 10, 0, 0]
        },
        label: {
          show: true,
          position: isHorizontalLayout ? 'right' : 'top',
          fontSize: 14,
          fontWeight: 600,
          color: theme.palette.text.secondary,
          formatter: (params: any) => {
            if (params.value == null) {
              return ''
            }
            return isPercentChart ? `${formatValue(params.value)}%` : `${formatValue(params.value)}`
          }
        },
        barMaxWidth: isHorizontalLayout ? 42 : 72
      }
    ]
  }

  return (
    <Card>
      <CardContent>
        {titleComponent}
        {/* Match base-chart overflow behavior by expanding into a scroll container when needed. */}
        <Box sx={{ width: '100%', overflowX: 'auto', overflowY: 'hidden', pb: 1 }}>
          <Box sx={{ minWidth: chartWidth }}>
            <ReactECharts
              lazyUpdate
              style={{ width: '100%', height: `${chartHeight}px` }}
              option={option}
              opts={{ renderer: 'svg' }}
            />
          </Box>
        </Box>
        {statisticBox}
      </CardContent>
    </Card>
  )
}

export default StatComparisonChart
